import { Alert, AlertTitle, Button, Divider, MenuItem, Snackbar, TextField, Typography } from '@mui/material'
import { ArrowDownward, ArrowForward } from '@mui/icons-material'
import { salesOrderLineType } from '../types'
import { useContext, useState } from 'react'
import { trackingType } from '../types'
import './EditLines.css'
import { DatePicker, NumberField } from '../helper'
import dayjs from 'dayjs'
import { dataContext } from '../index'

function EditLines(props: {
  lines: salesOrderLineType[]
  trackingNumbers: trackingType[]
  setParentTrackingNumbers: (trackingNumbers: trackingType[]) => void
  updateLines: (updates: Partial<salesOrderLineType>[]) => Promise<void>
  setOpen: (open: boolean) => void
  handleAddTrackingNumber: (lineID: string, newTrackingNumber: trackingType) => Promise<void>
  handleEditTrackingNumbers: (
    lineID: string,
    newTrackingNumber: (trackingType & { originalTrackingNumber: string })[],
  ) => Promise<void>
}) {
  const { user } = useContext(dataContext)
  const [newEtaDate, setNewEtaDate] = useState('')
  const [newTrackingNumber, setNewTrackingNumber] = useState<trackingType>({
    tracking_number: '',
    ship_method: '',
    quantity_shipped: null,
    ship_date: null,
    delivery_date: null,
    person_signed: '',
  } as trackingType)
  const [editTrackingNumber, setEditTrackingNumber] = useState<trackingType & { originalTrackingNumber: string }>({
    tracking_number: '',
    ship_method: '',
    quantity_shipped: null,
    ship_date: null,
    delivery_date: null,
    person_signed: '',
    originalTrackingNumber: '',
  } as trackingType & { originalTrackingNumber: string })
  const [selectedEditTrackingNumber, setSelectedEditTrackingNumber] = useState<string>('')
  const [updateMade, setUpdateMade] = useState<null | 'add tracking' | 'update tracking' | 'update eta'>(null)
  const [updateError, setUpdateError] = useState<Set<'add tracking' | 'update tracking' | 'update eta'>>(new Set())

  const handleUpdateEta = () => {
    const updates: Partial<salesOrderLineType>[] = []
    props.lines.forEach(line => {
      updates.push({
        sales_order_txn_id: line.sales_order_txn_id,
        sales_order_line_txn_id: line.sales_order_line_txn_id,
        eta_date_current: newEtaDate,
        eta_date_previous: line.eta_date_current,
      })
    })
    props
      .updateLines(updates)
      .then(() => {
        setUpdateMade('update eta')
        setUpdateError(prev => new Set([...prev].filter(e => e !== 'update eta')))
        setNewEtaDate('')
      })
      .catch(err => {
        setUpdateError(prev => new Set([...prev, 'update eta']))
        console.error(err)
      })
  }

  return (
    <div className="edit-lines">
      <div className="edit-lines-header-container">
        <div className="edit-lines-header">
          <Typography variant="h6">Bulk Edit</Typography>
          <Button onClick={() => props.setOpen(false)}>
            <ArrowForward />
          </Button>
        </div>
        <Divider />
      </div>
      <div className="edit-lines-body">
        <Alert severity="info">
          <AlertTitle>
            {props.lines.length} item{props.lines.length === 1 ? '' : 's'} selected for editing
          </AlertTitle>
          All {props.lines.length} item{props.lines.length === 1 ? '' : 's'} will be updated to reflect any changes
        </Alert>
        {user.user_type !== 'Sales Rep' && (
          <>
            <div className="edit-lines-add-tracking">
              <Typography variant="body1">Add New Tracking Number:</Typography>
              <div className="text-fields-grid">
                <TextField
                  size="small"
                  label="Tracking Number *"
                  value={newTrackingNumber.tracking_number}
                  onChange={e => setNewTrackingNumber({ ...newTrackingNumber, tracking_number: e.target.value })}
                />
                <TextField
                  size="small"
                  label="Ship Method"
                  value={newTrackingNumber.ship_method}
                  onChange={e => setNewTrackingNumber({ ...newTrackingNumber, ship_method: e.target.value })}
                />
                <NumberField
                  size="small"
                  label="Qty Shipped"
                  value={newTrackingNumber.quantity_shipped ?? null}
                  onChange={e => setNewTrackingNumber({ ...newTrackingNumber, quantity_shipped: e ?? null })}
                />
                <DatePicker
                  size="small"
                  label="Ship Date"
                  value={newTrackingNumber.ship_date ? dayjs.utc(newTrackingNumber.ship_date) : null}
                  onChange={e => setNewTrackingNumber({ ...newTrackingNumber, ship_date: e?.toDate() ?? null })}
                />
                <DatePicker
                  size="small"
                  label="Delivery Date"
                  value={newTrackingNumber.delivery_date ? dayjs.utc(newTrackingNumber.delivery_date) : null}
                  onChange={e => setNewTrackingNumber({ ...newTrackingNumber, delivery_date: e?.toDate() ?? null })}
                />
                <TextField
                  size="small"
                  label="Person Signed"
                  value={newTrackingNumber.person_signed}
                  onChange={e => setNewTrackingNumber({ ...newTrackingNumber, person_signed: e.target.value })}
                />
              </div>
              <div className="edit-lines-error-button-container">
                {updateError.has('add tracking') && (
                  <Alert severity="error">
                    <AlertTitle>Error</AlertTitle>
                    There was a problem adding the tracking number. Please try again or contact support if the error
                    persists.
                  </Alert>
                )}
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => {
                    props.lines.forEach(line => {
                      props
                        .handleAddTrackingNumber(line.sales_order_line_txn_id, newTrackingNumber)
                        .then(() => {
                          setUpdateMade('add tracking')
                          setUpdateError(prev => new Set([...prev].filter(e => e !== 'add tracking')))
                          setNewTrackingNumber({
                            tracking_number: '',
                            ship_method: '',
                            quantity_shipped: null,
                            ship_date: null,
                            delivery_date: null,
                            person_signed: '',
                          } as trackingType)
                        })
                        .catch(err => {
                          setUpdateError(prev => new Set([...prev, 'add tracking']))
                          console.error(err)
                        })
                    })
                  }}
                  disabled={!newTrackingNumber.tracking_number}
                >
                  Add
                </Button>
              </div>
            </div>
            <div className="edit-lines-update-tracking">
              <Typography variant="body1">Update Tracking Number</Typography>
              <div className="edit-lines-update-tracking-select">
                <TextField
                  size="small"
                  label="Existing Tracking Number"
                  select
                  fullWidth
                  value={selectedEditTrackingNumber}
                  onChange={e => {
                    setSelectedEditTrackingNumber(e.target.value)
                    setEditTrackingNumber({
                      ...props.trackingNumbers.filter(tn => tn.tracking_number === e.target.value)[0],
                      originalTrackingNumber: e.target.value,
                    })
                  }}
                >
                  {[...new Set(props.trackingNumbers.map(tn => tn.tracking_number))].map(trackingNumber => (
                    <MenuItem key={trackingNumber} value={trackingNumber}>
                      {trackingNumber}
                    </MenuItem>
                  ))}
                </TextField>

                <ArrowDownward />
              </div>
              <div className="text-fields-grid">
                <TextField
                  size="small"
                  label="New Tracking Number *"
                  value={editTrackingNumber.tracking_number}
                  onChange={e => setEditTrackingNumber({ ...editTrackingNumber, tracking_number: e.target.value })}
                />
                <TextField
                  size="small"
                  label="Ship Method"
                  value={editTrackingNumber.ship_method}
                  onChange={e => setEditTrackingNumber({ ...editTrackingNumber, ship_method: e.target.value })}
                />
                <DatePicker
                  size="small"
                  label="Ship Date"
                  value={editTrackingNumber.ship_date ? dayjs.utc(editTrackingNumber.ship_date) : null}
                  onChange={e => setEditTrackingNumber({ ...editTrackingNumber, ship_date: e?.toDate() ?? null })}
                />
                <DatePicker
                  size="small"
                  label="Delivery Date"
                  value={editTrackingNumber.delivery_date ? dayjs.utc(editTrackingNumber.delivery_date) : null}
                  onChange={e => setEditTrackingNumber({ ...editTrackingNumber, delivery_date: e?.toDate() ?? null })}
                />
                <TextField
                  size="small"
                  label="Person Signed"
                  value={editTrackingNumber.person_signed}
                  onChange={e => setEditTrackingNumber({ ...editTrackingNumber, person_signed: e.target.value })}
                />
              </div>
              <div className="edit-lines-error-button-container">
                {updateError.has('update tracking') && (
                  <Alert severity="error">
                    <AlertTitle>Error</AlertTitle>
                    There was a problem updating the tracking number. Please try again or contact support if the error
                    persists.
                  </Alert>
                )}
                <Button
                  onClick={() => {
                    props.trackingNumbers.forEach(tn => {
                      props
                        .handleEditTrackingNumbers(tn.sales_order_line_txn_id, [
                          { ...editTrackingNumber, quantity_shipped: tn.quantity_shipped },
                        ])
                        .then(() => {
                          setUpdateMade('update tracking')
                          setUpdateError(prev => new Set([...prev].filter(e => e !== 'update tracking')))
                          setSelectedEditTrackingNumber('')
                          setEditTrackingNumber({
                            tracking_number: '',
                            ship_method: '',
                            quantity_shipped: null,
                            ship_date: null,
                            delivery_date: null,
                            person_signed: '',
                            originalTrackingNumber: '',
                          } as trackingType & { originalTrackingNumber: string })
                        })
                        .catch(err => {
                          setUpdateError(prev => new Set([...prev, 'update tracking']))
                          console.error(err)
                        })
                    })
                  }}
                  size="large"
                  variant="contained"
                  disabled={!editTrackingNumber.tracking_number}
                >
                  Update
                </Button>
              </div>
            </div>
          </>
        )}
        <div className="edit-lines-update-eta">
          <Typography variant="body1">Update ETA Date(s)</Typography>
          <div className="edit-lines-update-eta-select">
            <DatePicker
              size="small"
              label="New ETA Date"
              value={newEtaDate ? dayjs.utc(newEtaDate) : null}
              onChange={e => setNewEtaDate(e?.format('MM/DD/YYYY') ?? '')}
            />
          </div>
          <div className="edit-lines-error-button-container">
            {updateError.has('update eta') && (
              <Alert severity="error">
                <AlertTitle>Error</AlertTitle>
                There was a problem updating the ETA date. Please try again or contact support if the error persists.
              </Alert>
            )}
            <Button onClick={handleUpdateEta} size="large" variant="contained" disabled={!newEtaDate}>
              Update
            </Button>
          </div>
        </div>
      </div>
      {updateMade && (
        <Snackbar
          open={!!updateMade}
          autoHideDuration={5000}
          onClose={() => setUpdateMade(null)}
          message={`${
            updateMade === 'add tracking'
              ? 'Tracking number added'
              : updateMade === 'update tracking'
              ? 'Tracking number updated'
              : 'ETA date updated'
          } successfully`}
        />
      )}
    </div>
  )
}

export default EditLines
